/**
 * Rolex CMP bridge — syncs the Consentmo consent state to the Rolex tracking cookies.
 *
 *   rlx-consent    <- Statistics / Analytics consent   (gates Adobe Analytics)
 *   rlx-marketing  <- Marketing / Targeting consent     (gates CO-OP / Google Ads)
 *
 * Rolex 2026 spec: a missing cookie must be treated as "not accepted" (tracking
 * blocked), the value must reflect the user's granular choice, and it must update
 * whenever consent changes during the visit.
 *
 * The Consentmo build installed on this store no longer dispatches the legacy
 * `consentmoSignal` DOM event that the previous bridge relied on (verified live:
 * accept/decline/setTrackingConsent all leave the event unfired, so the cookie was
 * stuck at "false"). We instead read Consentmo's authoritative permission API
 * (`analyticsAllowed()` / `marketingAllowed()`) and reconcile on every
 * consent-affecting signal the build does emit (`consentmo:dialogClosed`), with a
 * short readiness poll and a visibility fallback.
 */
(function () {
  var CONSENT_COOKIE = 'rlx-consent';
  var MARKETING_COOKIE = 'rlx-marketing';
  var COOKIE_DAYS = 365;

  function createCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toGMTString();
    }
    document.cookie = name + '=' + value + expires + '; path=/; SameSite=Lax';
  }

  function readCookie(name) {
    var nameEQ = name + '=';
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  function notifyCorner(msg) {
    var corner = document.getElementById('rlx-corner');
    if (corner && corner.contentWindow) {
      corner.contentWindow.postMessage(msg, 'https://cornersv7.rolex.com');
    }
  }

  // Write (and notify the e-Corner iframe) only when the value actually changes,
  // so there is never more than one cookie and we avoid redundant postMessages.
  function setConsentCookie(name, granted) {
    var value = granted ? 'true' : 'false';
    if (readCookie(name) === value) return false;
    createCookie(name, value, COOKIE_DAYS);
    return true;
  }

  function applyConsent(analyticsGranted) {
    if (!setConsentCookie(CONSENT_COOKIE, analyticsGranted)) return;
    if (analyticsGranted) {
      notifyCorner('consentTrue');
      notifyCorner('consentValidation');
    } else {
      notifyCorner('consentFalse');
    }
  }

  function applyMarketing(marketingGranted) {
    if (!setConsentCookie(MARKETING_COOKIE, marketingGranted)) return;
    notifyCorner(marketingGranted ? 'marketingTrue' : 'marketingFalse');
  }

  // Default state: an absent cookie means consent has NOT been accepted, so tracking
  // stays blocked until the user explicitly opts in (Rolex 2026 requirement).
  if (readCookie(CONSENT_COOKIE) === null) applyConsent(false);
  if (readCookie(MARKETING_COOKIE) === null) applyMarketing(false);

  // Pull the authoritative consent state from Consentmo, if the CMP is ready.
  function reconcile() {
    var C = window.Consentmo;
    if (!C || typeof C.analyticsAllowed !== 'function' || typeof C.marketingAllowed !== 'function') {
      return false;
    }
    try {
      applyConsent(C.analyticsAllowed() === true);
      applyMarketing(C.marketingAllowed() === true);
    } catch (e) {
      return false;
    }
    return true;
  }

  // Reconcile on every consent signal. This build reliably emits
  // `consentmo:dialogClosed` when the user finishes choosing; we also register the
  // legacy/self-reported event names in case a future Consentmo build restores them.
  var eventNames = ['consentmo:dialogClosed', 'consentmoSignal', 'consentmoSignal_onLoad'];
  try {
    if (window.Consentmo && window.Consentmo.events) {
      Object.keys(window.Consentmo.events).forEach(function (k) {
        var n = String(window.Consentmo.events[k]);
        if (eventNames.indexOf(n) === -1) eventNames.push(n);
      });
    }
  } catch (e) {}
  eventNames.forEach(function (n) {
    document.addEventListener(n, reconcile);
  });
  document.addEventListener('visibilitychange', reconcile);

  // Reconcile heartbeat. The events above make updates instant when they fire, but
  // this Consentmo build only emits `consentmo:dialogClosed` while a dialog is open,
  // so we also poll: fast (500ms) until the CMP is ready and the stored consent is
  // synced, then a steady low-frequency beat so any later consent change is mirrored
  // to the cookies within ~1.5s regardless of which signals the build emits.
  var ready = false;
  function tick() {
    if (reconcile()) ready = true;
    setTimeout(tick, ready ? 1500 : 500);
  }
  tick();
})();
